/**
 * L4-KILL-003: KillNotification React Component
 *
 * Single kill notification showing victor and defeated creature portraits.
 * Displays with gold highlight for victor (left) and red highlight for defeated (right).
 *
 * @see docs/specs/L4-TASKS/kill-notification/L4-KILL-003-kill-notification-component.md
 */

import React from 'react';

/**
 * Kill notification data structure
 */
export interface KillNotificationData {
  id: string;
  victorUnitId: string;
  victorCreatureId: string;
  victorPortraitUrl: string;
  victorCreatureName: string;
  defeatedUnitId: string;
  defeatedCreatureId: string;
  defeatedPortraitUrl: string;
  defeatedCreatureName: string;
  timestamp: number;
  fadeState: 'entering' | 'visible' | 'fading' | 'hidden';
}

export interface KillNotificationProps {
  notification: KillNotificationData;
}

// Styles
const getContainerStyle = (fadeState: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 16px',
  background: 'linear-gradient(135deg, rgba(20, 20, 30, 0.9) 0%, rgba(30, 30, 45, 0.85) 100%)',
  borderRadius: 12,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
  pointerEvents: 'auto',
  opacity: fadeState === 'entering' || fadeState === 'fading' ? 0 : 1,
  transform: fadeState === 'entering' ? 'translateX(-30px)' : fadeState === 'fading' ? 'translateX(-20px)' : 'translateX(0)',
  transition: 'opacity 0.5s ease-out, transform 0.3s ease-out',
});

const portraitBaseStyle: React.CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: '50%',
  overflow: 'hidden',
  borderWidth: 3,
  borderStyle: 'solid',
  flexShrink: 0,
};

const victorPortraitStyle: React.CSSProperties = {
  ...portraitBaseStyle,
  borderColor: '#ffd700',
  boxShadow: '0 0 12px rgba(255, 215, 0, 0.5), 0 0 24px rgba(255, 215, 0, 0.2)',
};

const defeatedPortraitStyle: React.CSSProperties = {
  ...portraitBaseStyle,
  borderColor: '#cc3333',
  boxShadow: '0 0 12px rgba(204, 51, 51, 0.5), 0 0 24px rgba(204, 51, 51, 0.2)',
};

const portraitImageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const dividerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  flexShrink: 0,
  color: '#aaa',
};

/**
 * Single kill notification showing victor and defeated creature portraits
 */
export const KillNotification: React.FC<KillNotificationProps> = ({
  notification,
}) => {
  return (
    <div
      style={getContainerStyle(notification.fadeState)}
      role="alert"
      aria-live="polite"
      aria-label={`${notification.victorCreatureName} defeated ${notification.defeatedCreatureName}`}
      data-testid="kill-notification"
    >
      {/* Victor portrait - gold highlight */}
      <div style={victorPortraitStyle}>
        <img
          src={notification.victorPortraitUrl}
          alt={notification.victorCreatureName}
          style={portraitImageStyle}
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/assets/ui/portrait-placeholder.png';
          }}
        />
      </div>

      {/* Crossed swords icon */}
      <div style={dividerStyle} aria-hidden="true">
        <CrossedSwordsIcon />
      </div>

      {/* Defeated portrait - red highlight */}
      <div style={defeatedPortraitStyle}>
        <img
          src={notification.defeatedPortraitUrl}
          alt={notification.defeatedCreatureName}
          style={portraitImageStyle}
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/assets/ui/portrait-placeholder.png';
          }}
        />
      </div>
    </div>
  );
};

/**
 * Crossed swords SVG icon
 */
const CrossedSwordsIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="28"
    height="28"
    fill="currentColor"
    style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))' }}
  >
    {/* Sword 1 (left to right) */}
    <path d="M6.92 5.51l3.53 3.53 1.41-1.41-3.53-3.53L10.44 2l-6.36 6.36 2.12 2.12 1.41-1.41-.7-.7 1.41-1.41.7.7 1.42-1.41-.7-.7 1.41-1.42-.7-.7z" />
    {/* Sword 2 (right to left) */}
    <path d="M17.08 5.51l-3.53 3.53-1.41-1.41 3.53-3.53L13.56 2l6.36 6.36-2.12 2.12-1.41-1.41.7-.7-1.41-1.41-.7.7-1.42-1.41.7-.7-1.41-1.42.7-.7z" />
    {/* Sword handles */}
    <path d="M12 10.586l-2.828 2.828 1.414 1.414L12 13.414l1.414 1.414 1.414-1.414L12 10.586z" />
    <path d="M12 14.828l-1.414 1.414L12 17.656l1.414-1.414L12 14.828z" />
  </svg>
);

export default KillNotification;
